import { useState } from 'react';
import {
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
} from '@staff-app/ui';
import type { DeviceRmaRequest } from '@staff-app/shared-types';
import { ExternalLink, PackagePlus, Pencil, Printer, Truck, Undo2 } from 'lucide-react';
import {
  useCreateDeviceRmaReplacementOrder,
  useSetDeviceRmaReplacementOrder,
  useSetDeviceRmaVerified,
} from '../hooks/useRma';
import { CopyButton } from '@/components/CopyButton';
import { RmaShipToEditor } from './RmaShipToEditor';
import { RmaWarrantyPanel } from './RmaWarrantyPanel';

/**
 * RmaVerifyDialog — confirm eligibility, then act on it.
 *
 * WHY A DIALOG AND NOT A ONE-CLICK TOGGLE (requester, 2026-08-25). Verifying an RMA is what
 * releases a replacement device and a prepaid return label. That is a shipment and a real cost, and
 * the fee that rides on getting it wrong is $50 / $110 / $220 by model. A toggle invites a
 * tick-through; a dialog asks for the two things the decision actually rests on — which order the
 * device came from, and what troubleshooting was done — and makes confirming a deliberate act.
 *
 * THE QUANTITY IS EDITABLE FOR THE SAME REASON AS THE ORDER NUMBER (requester, 2026-09-21), and
 * it is stored the same way: `deviceCount` is what the customer typed into HubSpot and the poll
 * rewrites it every five minutes, so the verifier's figure lands in `verifiedDeviceCount` and the
 * customer's survives beside it. It is the number that reaches Shopify — a replacement of two when
 * the fault is one device is a device and a freight leg given away, and this dialog is the last
 * place a human looks at the facts before that happens.
 *
 * THE ORDER NUMBER IS PREFILLED BUT EDITABLE, and that is the point of it. `orderNumber` is typed
 * by the customer into a free-text field and is routinely partial or wrong; the staffer corrects it
 * against Shopify. The correction is stored in `previousOrderNumber`, a SEPARATE column, so what
 * the customer submitted survives as evidence of the discrepancy rather than being overwritten.
 *
 * THE REPLACEMENT BUTTON IS LIVE (2026-09-21) AND GATED ON THE VERIFICATION. Pressing it creates
 * a $0 Shopify order for the model's replacement SKU, to the address on the request. It is
 * disabled until the row is verified AND inside the 13-month warranty — not as a UI nicety, but
 * because the verification IS the authorisation and the warranty IS the entitlement; the server
 * refuses either case identically. It is NOT gated on whether
 * Shopify will accept the call: nothing here can re-probe a grant, so the press goes through and
 * any refusal is shown verbatim. `write_orders` + `read_products` were granted on 2026-09-21, so
 * pressing this now creates a REAL order against the live store — there is no test mode on the
 * Shopify side, and the only undo is cancelling the order in the admin.
 *
 * CREATING THE ORDER IS NOT SHIPPING IT. Nothing is picked and no carrier is booked by
 * `orderCreate`; the shipping label is made in the Shopify admin, by a human, afterwards. The
 * dialog says so and links straight to the order rather than leaving a plausible-looking gap where
 * an operator assumes a device is on its way. The tracking number is how that loop closes.
 *
 * The RETURN column stays INERT — buying a carrier label needs a carrier account staff-app does
 * not have. It says so on itself, because a button that looks live and silently does nothing is
 * worse than one that explains why it cannot run.
 */
export function RmaVerifyDialog({
  row,
  onClose,
}: {
  row: DeviceRmaRequest;
  onClose: () => void;
}) {
  const [setVerified, { loading }] = useSetDeviceRmaVerified();
  const [createOrder, { loading: creatingOrder }] = useCreateDeviceRmaReplacementOrder();
  const [recordOrder, { loading: recordingOrder }] = useSetDeviceRmaReplacementOrder();
  const [error, setError] = useState<string | null>(null);
  // Prefilled from the staffer's own earlier correction if there is one, else from what the
  // customer submitted. Never blank when either exists.
  const [orderNo, setOrderNo] = useState(row.previousOrderNumber ?? row.orderNumber ?? '');
  const [notes, setNotes] = useState(row.troubleshootingNotes ?? '');
  // Same prefill rule as the order number: the staffer's own earlier correction if there is one,
  // else what the customer submitted. Held as a STRING so the field can be emptied — a number
  // state would have to represent "empty" as 0 or NaN, and 0 already means something else here.
  const [qty, setQty] = useState(
    String(row.verifiedDeviceCount ?? row.deviceCount ?? ''),
  );
  // The manual lane. Closed by default: it is the exception, and an always-open pair of inputs
  // beside a button that does the same job invites the wrong one.
  // What the create mutation just returned, held so the confirmation can be shown IMMEDIATELY —
  // read from the mutation's own result rather than waiting for the list refetch to re-render this
  // dialog from the parent. The operator pressed a button that ships hardware; the answer should
  // not arrive a network round-trip later than the act.
  // The WHOLE row the mutation returned, not a few fields of it: the server's answer already
  // carries the stamp, the manual flag and any tracking it picked up, and reconstructing a partial
  // row from three of them is how the confirmation ended up reading "Released by unknown".
  const [created, setCreated] = useState<DeviceRmaRequest | null>(null);
  // `YYYY-MM-DD`, owned here so Confirm saves it with the stamp — the date is evidence FOR the
  // verification, not a separate decision, which is why it rides the same button as the order
  // number and the quantity rather than having a Save of its own.
  const [orderDate, setOrderDate] = useState(row.verifiedOrderDate ?? '');
  const [manual, setManual] = useState(false);
  const [manualOrder, setManualOrder] = useState(row.replacementOrderName ?? '');
  const [manualTracking, setManualTracking] = useState(row.replacementTrackingNumber ?? '');

  /**
   * The row as this dialog should DRAW it — the prop, plus whatever the create mutation just
   * returned.
   *
   * The page hands this dialog a snapshot taken when the button was pressed, and re-points it at
   * fresh data once the refetch lands. That leaves a window, a network round-trip wide, in which
   * the order exists and the panel still offers to create one — which is how "Create RMA order"
   * stayed on screen after an order had been made (requester, 2026-09-21). Reading the mutation's
   * own answer closes it immediately; the refetch then agrees a moment later.
   */
  const shown: DeviceRmaRequest = created ?? row;

  /**
   * May this operator record a replacement at all?
   *
   * The same rule as the Create button, applied to the manual lane (requester, 2026-09-21): typing
   * an order number in asserts a replacement is owed, which is precisely the claim the verification
   * stands behind. Without this, the manual form was the way around the gate.
   *
   * An EXISTING order keeps the lane open even on an unverified row — a stamp can be retracted
   * after an order was created (RMA-1007 was in exactly that state), and that row still needs its
   * tracking number and still needs to be correctable. The server draws the same line.
   */
  const mayRecordReplacement = shown.verified || Boolean(shown.replacementOrderId);

  /**
   * May a replacement be CREATED? Verified, and inside the 13-month warranty (requester,
   * 2026-09-22: "if the warranty expired and unknown, it should not allow to create the RMA").
   *
   * Judged from `warrantyState` on the SAVED row, not from the date currently in the panel, because
   * that is what the server judges — a button that looked enabled on an unsaved date would press
   * straight into a refusal. So a verifier who has just looked a date up must save it first, which
   * the panel already tells them to do.
   *
   * The MANUAL lane is deliberately not gated this way: recording an order somebody already raised
   * by hand describes what happened, and a goodwill replacement is exactly that case.
   */
  const warrantyOk = shown.warrantyState === 'IN_WARRANTY';
  const mayCreateReplacement = shown.verified && warrantyOk;

  const parsedQty = qty.trim() === '' ? 0 : Number(qty);
  const qtyToSend = Number.isInteger(parsedQty) && parsedQty >= 0 ? parsedQty : undefined;
  /** What a replacement would actually ship today — the correction if there is one, else theirs. */
  const effectiveQty = row.verifiedDeviceCount ?? row.deviceCount ?? 1;

  const submit = async (verified: boolean) => {
    setError(null);
    try {
      await setVerified({
        variables: {
          submissionId: row.submissionId,
          verified,
          // Sent on both paths: an un-verify should still keep the staffer's corrections, and the
          // server treats an omitted field as "leave alone" rather than "clear".
          previousOrderNumber: orderNo,
          troubleshootingNotes: notes,
          // '' means the operator cleared the field, which the server reads as "drop my
          // correction, the customer's figure applies again" — that is what 0 encodes. A value
          // that will not parse is sent as undefined (leave it alone) rather than as a guess.
          deviceCount: qtyToSend,
          // '' clears it, same partial contract as the rest. An unreadable value is refused by the
          // server rather than dropped, so a typo cannot quietly leave the warranty unjudged.
          orderDate,
        },
      });
      onClose();
    } catch (e) {
      setError((e as Error).message);
    }
  };

  /**
   * Release the replacement.
   *
   * Does NOT close the dialog on success — unlike confirming eligibility, which is the end of a
   * decision. This one produces something the operator wants to SEE: the order number appears in
   * place of the button, with the link through to Shopify. Closing would hide the only evidence
   * that anything shipped.
   *
   * The row is not re-read locally; the mutation refetches the list and the parent re-renders this
   * dialog from the server's answer.
   */
  /**
   * Record an order raised by hand, and/or its tracking number.
   *
   * Sends BOTH fields every time, which is safe because the server's contract is partial by VALUE
   * rather than by presence: '' clears, a value sets. So an operator who fixes a typo in the
   * tracking number does not have to think about whether the order field is about to be resent.
   */
  const saveManual = async () => {
    setError(null);
    try {
      await recordOrder({
        variables: {
          submissionId: row.submissionId,
          orderNumber: manualOrder.trim(),
          trackingNumber: manualTracking.trim(),
        },
      });
      setManual(false);
    } catch (e) {
      setError((e as Error).message);
    }
  };

  const releaseReplacement = async () => {
    setError(null);
    try {
      const res = await createOrder({ variables: { submissionId: row.submissionId } });
      const out = res.data?.createDeviceRmaReplacementOrder;
      if (out?.replacementOrderId) setCreated(out);
    } catch (e) {
      setError((e as Error).message);
    }
  };

  /**
   * THE CONFIRMATION (requester, 2026-09-21: "once I click Create the RMA Order, can you pop out
   * the replacement order number?").
   *
   * A second dialog over the first, rather than the order number quietly taking the button's place
   * further down the panel. The press is the moment a device is committed, and the number it
   * produces is what the operator writes on a ticket, reads down a phone and searches Shopify by —
   * so it is shown big, monospaced and copyable, not rendered into a corner of a form they have
   * already scrolled past.
   *
   * It carries the NEXT STEP as its primary action, because the order is not a shipment: Shopify
   * still needs a label. "Done" is the quiet option, deliberately — a confirmation whose loudest
   * button is "close" teaches people that the work ended here, and it did not.
   */
  const confirmation = created ? (
    <Dialog open onOpenChange={(o) => !o && setCreated(null)}>
      {/* Small, but bounded for the same reason as its parent: on a short laptop window in a
          scaled display, even this can exceed the viewport, and a confirmation you cannot read to
          the end is the one dialog that must not clip — it carries the order number. */}
      <DialogContent
        className="flex max-h-[85vh] max-w-md flex-col overflow-y-auto"
        data-testid="rma-created-dialog"
      >
        <DialogHeader>
          <DialogTitle>{'Replacement order created'}</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-4">
          <div className="flex items-center justify-center gap-2 rounded-md border bg-muted/40 py-4">
            <span className="font-mono text-2xl" data-testid="rma-created-number">
              {created.replacementOrderName}
            </span>
            <CopyButton
              value={created.replacementOrderName ?? ''}
              label="Order number"
              testId="rma-created-copy"
            />
          </div>
          <p className="text-sm text-muted-foreground" data-testid="rma-created-next">
            {'Nothing has shipped yet — Shopify still needs a shipping label for this order. Create it there, then save the tracking number on this RMA.'}
          </p>
          <div className="flex items-center justify-end gap-2">
            <Button variant="ghost" onClick={() => setCreated(null)} data-testid="rma-created-close">
              {'Done'}
            </Button>
            {created.replacementOrderUrl ? (
              <Button asChild className="gap-1">
                <a
                  href={created.replacementOrderUrl}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="rma-created-open"
                >
                  <Truck className="h-4 w-4" />
                  {'Create shipping label'}
                  <ExternalLink className="h-3 w-3" />
                </a>
              </Button>
            ) : null}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  ) : null;

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      {confirmation}
      {/* BOUNDED AND SCROLLABLE. The shared DialogContent is `overflow-hidden` with no max height,
          so a dialog taller than the viewport simply runs off the bottom of the screen with no way
          to reach the rest — which this one now does on a laptop, having grown a warranty panel, a
          ship-to editor and a manual-entry form. 85vh leaves the backdrop visible so it still reads
          as a dialog rather than a page. */}
      <DialogContent
        className="flex max-h-[85vh] max-w-3xl flex-col"
        data-testid="rma-verify-dialog"
      >
        <DialogHeader>
          <DialogTitle>
            {row.verified ? 'RMA fulfillment' : 'Confirm RMA eligibility'}
          </DialogTitle>
        </DialogHeader>

        {/* ONLY THE BODY SCROLLS. The decision buttons stay pinned below it: on a long request an
            operator would otherwise have to scroll past the fulfilment columns to find Confirm, and
            "where did the button go" is how a half-finished verification gets abandoned.
            `-mr-2 pr-2` keeps the scrollbar off the content instead of over it. */}
        <div className="-mr-2 flex flex-col gap-4 overflow-y-auto pr-2">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <Label htmlFor="rma-prev-order">{'Previous order #'}</Label>
              <Input
                id="rma-prev-order"
                value={orderNo}
                onChange={(e) => setOrderNo(e.target.value)}
                placeholder="e.g. #10234"
                className="mt-1 font-mono"
                data-testid="rma-dialog-order"
              />
              {/* Shown only when the staffer has changed it — the discrepancy IS the useful
                  information, and repeating an unchanged value would just be noise. */}
              {row.orderNumber && orderNo.trim() !== row.orderNumber ? (
                <p className="mt-1 text-xs text-muted-foreground" data-testid="rma-dialog-order-submitted">
                  {`Customer submitted: ${row.orderNumber}`}
                </p>
              ) : null}
            </div>

            <div>
              <Label htmlFor="rma-qty">{'Units to replace'}</Label>
              {/* TEXT, not `type="number"`, and the reason is this field specifically: a number
                  input changes its value on a SCROLL WHEEL when focused, and this number becomes
                  free hardware — a stray scroll turning 1 into 11 would be ten devices nobody
                  decided to send. `inputMode="numeric"` still brings up the numeric keypad on a
                  phone; the server is the validator either way (it refuses non-integers and
                  anything over 100), which is where a cap on free hardware belongs. */}
              <Input
                id="rma-qty"
                inputMode="numeric"
                value={qty}
                onChange={(e) => setQty(e.target.value.replace(/[^0-9]/g, ''))}
                placeholder="e.g. 1"
                className="mt-1 font-mono"
                data-testid="rma-dialog-qty"
              />
              {/* The model is context for the number, not a field of its own — it is decided by the
                  form the customer used and nothing in this dialog can change it. */}
              <p className="mt-1 text-xs text-muted-foreground">
                {row.deviceModel}
                {/* Shown only on a discrepancy, like the order number's: an unchanged value
                    repeated underneath itself is noise. */}
                {row.deviceCount != null && String(row.deviceCount) !== qty.trim() ? (
                  <span data-testid="rma-dialog-qty-submitted">
                    {` · customer submitted ${row.deviceCount}`}
                  </span>
                ) : null}
              </p>
            </div>
          </div>

          <div>
            <Label htmlFor="rma-notes">{'Ticket / troubleshooting'}</Label>
            <textarea
              id="rma-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={4}
              placeholder="What troubleshooting was done, and the ticket it happened on. The RMA gate turns on this — and so does whether a restocking fee applies."
              className="mt-1 w-full rounded-md border bg-background p-2 text-sm"
              data-testid="rma-dialog-notes"
            />
            {row.issueDescription ? (
              <p className="mt-1 whitespace-pre-wrap text-xs text-muted-foreground">
                <span className="font-medium">{'Customer reported: '}</span>
                {row.issueDescription}
              </p>
            ) : null}
          </div>

          {/* THE ORDER DATE AND WHAT IT MEANS, directly under the troubleshooting record
              (requester, 2026-09-22) — the two things a verifier weighs, side by side. Eligibility
              rests on "was it bought within 13 months" and this dialog used to make them leave to
              find out. */}
          <RmaWarrantyPanel
            row={row}
            orderNumber={orderNo}
            value={orderDate}
            onChange={setOrderDate}
          />

          {/* WHERE IT SHIPS, above the button that ships it. The order matters: an operator reads
              down this dialog and the last thing they should settle before releasing a device is
              the address it goes to — and unlike everything else here, correcting it is a separate
              save, because it is a different decision from eligibility. */}
          <RmaShipToEditor row={shown} />

          {/* The Return column is INERT pending capability that does not exist in this repo —
              stated on the button rather than hidden, so nobody presses a live-looking control and
              wonders why nothing shipped. */}
          <div className="grid grid-cols-1 gap-4 border-t pt-4 sm:grid-cols-2">
            <section className="flex flex-col gap-2" data-testid="rma-replacement-section">
              <h3 className="text-sm font-semibold">{'Replacement'}</h3>

              {/* ── ONE SKELETON FOR EVERY STATE (requester, 2026-09-22) ──────────────────────
                  status line → primary action → manual lane → explanation, always in that order
                  and always present. Before this the section grew extra elements once an order
                  existed — a status block, a second button, a next-step note — so "Create RMA
                  order" and "Enter order / tracking manually" sat at different heights depending on
                  the row, and an operator working a queue had to re-find the control every time.
                  Same four slots now; only what is IN them changes. */}

              {/* 1 · STATUS. Always a line, so nothing below it shifts. */}
              {shown.replacementOrderId ? (
                <div className="flex flex-col gap-0.5">
                  <a
                    href={shown.replacementOrderUrl ?? undefined}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1 font-mono text-sm underline"
                    data-testid="rma-replacement-order-link"
                  >
                    {shown.replacementOrderName || 'Open in Shopify'}
                    <ExternalLink className="h-3 w-3" />
                  </a>
                  <p className="text-xs text-muted-foreground" data-testid="rma-replacement-stamp">
                    {`${shown.replacementOrderManual ? 'Recorded' : 'Released'} by ${
                      shown.replacementOrderedBy ?? 'unknown'
                    }${
                      shown.replacementOrderedAt
                        ? ` on ${new Date(shown.replacementOrderedAt).toLocaleString()}`
                        : ''
                    }`}
                  </p>
                  {shown.replacementTrackingNumber ? (
                    <p className="text-xs" data-testid="rma-replacement-tracking">
                      {'Tracking: '}
                      {shown.replacementTrackingUrl ? (
                        <a
                          href={shown.replacementTrackingUrl}
                          target="_blank"
                          rel="noreferrer"
                          className="font-mono underline"
                        >
                          {shown.replacementTrackingNumber}
                        </a>
                      ) : (
                        <span className="font-mono">{shown.replacementTrackingNumber}</span>
                      )}
                    </p>
                  ) : null}
                </div>
              ) : (
                <p className="text-xs text-muted-foreground" data-testid="rma-replacement-none">
                  {'No replacement order yet.'}
                </p>
              )}

              {/* 2 · THE PRIMARY ACTION, in one slot. Which action it is depends on where the row
                  has got to — create the order, or go and make its shipping label — but it is
                  always the same control in the same place. */}
              {/* `self-start` on every button in this column: a flex column STRETCHES its children,
                  so a button fills the width and centres its own label — which on the borderless
                  ghost toggle reads as centred text in a left-aligned panel (spotted on RMA-1008).
                  Sized to content, they all start at the same left edge as the text around them. */}
              {shown.replacementOrderId ? (
                <Button asChild variant="outline" className="gap-1 self-start">
                  <a
                    href={shown.replacementOrderUrl ?? undefined}
                    target="_blank"
                    rel="noreferrer"
                    data-testid="rma-create-shipping-label"
                  >
                    <Truck className="h-4 w-4" />
                    {'Create shipping label in Shopify'}
                    <ExternalLink className="h-3 w-3" />
                  </a>
                </Button>
              ) : (
                <Button
                  variant="outline"
                  className="gap-1 self-start"
                  // Two conditions, both enforced by the server as well: the verification is the
                  // authorisation, and the 13-month warranty is the entitlement. This is the cheap
                  // half of each.
                  disabled={!mayCreateReplacement || creatingOrder}
                  onClick={() => void releaseReplacement()}
                  data-testid="rma-create-order"
                >
                  <PackagePlus className="h-4 w-4" />
                  {creatingOrder ? 'Creating…' : 'Create RMA order'}
                </Button>
              )}

              {/* 3 · THE MANUAL LANE, always the third thing. */}
              {!manual ? (
                <Button
                  variant="ghost"
                  size="sm"
                  className="gap-1 self-start"
                  disabled={!mayRecordReplacement}
                  onClick={() => setManual(true)}
                  data-testid="rma-manual-toggle"
                >
                  <Pencil className="h-4 w-4" />
                  {shown.replacementOrderId ? 'Edit order / tracking' : 'Enter order / tracking manually'}
                </Button>
              ) : (
                <div className="flex flex-col gap-2 rounded-md border p-2" data-testid="rma-manual-form">
                  <div>
                    <Label htmlFor="rma-manual-order" className="text-xs">
                      {'Shopify order #'}
                    </Label>
                    <Input
                      id="rma-manual-order"
                      value={manualOrder}
                      onChange={(e) => setManualOrder(e.target.value)}
                      placeholder="#28858"
                      className="mt-1 font-mono"
                      data-testid="rma-manual-order"
                    />
                  </div>
                  <div>
                    <Label htmlFor="rma-manual-tracking" className="text-xs">
                      {'Tracking #'}
                    </Label>
                    <Input
                      id="rma-manual-tracking"
                      value={manualTracking}
                      onChange={(e) => setManualTracking(e.target.value)}
                      placeholder="leave blank to take Shopify's"
                      className="mt-1 font-mono"
                      data-testid="rma-manual-tracking"
                    />
                  </div>
                  <p className="text-xs text-muted-foreground" data-testid="rma-manual-note">
                    {'The order number is checked against Shopify before it is saved — a near-miss is refused rather than recorded, because a mistyped number can match a different real order. Clearing both fields removes the record.'}
                  </p>
                  <div className="flex items-center justify-end gap-2">
                    <Button variant="ghost" size="sm" onClick={() => setManual(false)} data-testid="rma-manual-cancel">
                      {'Cancel'}
                    </Button>
                    <Button size="sm" disabled={recordingOrder} onClick={() => void saveManual()} data-testid="rma-manual-save">
                      {recordingOrder ? 'Checking…' : 'Save'}
                    </Button>
                  </div>
                </div>
              )}

              {/* 4 · WHY THE STATE IS WHAT IT IS — one paragraph, always last, so the controls
                  above it never move when the wording grows. */}
              <p className="text-xs text-muted-foreground" data-testid="rma-replacement-note">
                {shown.replacementOrderId
                  ? shown.replacementTrackingNumber
                    ? 'The replacement is on its way. Cancel the order in Shopify if it was raised in error — this dialog will not create a second one.'
                    : 'The order exists but nothing has shipped: Shopify still needs a shipping label. Create it there, then come back and save the tracking number — or leave the tracking field blank and we will take it from the order once Shopify has one.'
                  : !row.verified
                    ? 'Confirm eligibility first — the verification is what authorises a replacement to ship, and it gates recording one by hand for the same reason.'
                    : shown.warrantyState === 'EXPIRED'
                      ? `Not eligible: the 13-month warranty ended on ${shown.warrantyEndsOn}. If this is a goodwill replacement, raise the order in Shopify and record it with “Edit order / tracking” above.`
                      : !warrantyOk
                        ? 'Not eligible yet: no purchase date is saved on this request, so the warranty cannot be judged. Set the order date above and press Confirm eligible — then this button opens.'
                        : `Creates a real $0 Shopify order for ${effectiveQty} × this model’s replacement SKU, shipped to the address on this request. It uses the SAVED quantity — press Save first if you have just changed it. There is no undo here: cancelling means cancelling the order in Shopify.`}
              </p>
            </section>

            <section className="flex flex-col gap-2">
              <h3 className="text-sm font-semibold">{'Return'}</h3>
              <Button variant="outline" className="gap-1 self-start" disabled data-testid="rma-create-return-label">
                <Undo2 className="h-4 w-4" />
                {'Create return label'}
              </Button>
              <Button variant="ghost" size="sm" className="gap-1 self-start" disabled data-testid="rma-print-return-label">
                <Printer className="h-4 w-4" />
                {'Print return label'}
              </Button>
              <p className="text-xs text-muted-foreground">
                {'Needs a carrier account. staff-app has no carrier integration yet.'}
              </p>

              {/* Recording the RECEIPT is NOT here. It lives in RmaReturnDialog, opened by pressing
                  the status itself in the Return column — one control for one fact, and a
                  different event: eligibility is decided before a replacement ships, receipt is
                  observed weeks later, usually by someone else. */}
            </section>
          </div>

        </div>

        {/* OUTSIDE the scroll area, with the buttons: a refusal that scrolled away while the
            operator was reading the form would look like nothing happened. */}
        {error ? (
          <p className="shrink-0 text-sm text-destructive" data-testid="rma-dialog-error">
            {error}
          </p>
        ) : null}

        <div className="flex shrink-0 items-center justify-end gap-2 border-t pt-4">
            <Button variant="ghost" onClick={onClose} data-testid="rma-dialog-cancel">
              {'Cancel'}
            </Button>
            {row.verified ? (
              <Button
                variant="outline"
                disabled={loading}
                onClick={() => void submit(false)}
                data-testid="rma-dialog-unverify"
              >
                {'Clear verification'}
              </Button>
            ) : null}
            <Button
              disabled={loading}
              onClick={() => void submit(true)}
              data-testid="rma-dialog-confirm"
            >
              {row.verified ? 'Save' : 'Confirm eligible'}
            </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
